import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Camera, Save, Lock, ArrowLeft, User as UserIcon, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, SectionCard } from "@/components/ui-bits";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const inputCls =
  "w-full border border-slate-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-slate-900 bg-white";

const MAX_DIM = 256; // resize avatars to a 256x256 square cap
const JPEG_QUALITY = 0.82;

async function fileToResizedDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, MAX_DIM / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/jpeg", JPEG_QUALITY));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function Profile() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [name, setName] = useState(user?.name || "");
  const [avatarUrl, setAvatarUrl] = useState(user?.avatar_url || "");
  const [savingProfile, setSavingProfile] = useState(false);
  const [pwCurrent, setPwCurrent] = useState("");
  const [pwNew, setPwNew] = useState("");
  const [pwConfirm, setPwConfirm] = useState("");
  const [savingPw, setSavingPw] = useState(false);

  if (!user) return null;

  const onPickAvatar = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }
    try {
      const dataUrl = await fileToResizedDataUrl(f);
      setAvatarUrl(dataUrl);
    } catch {
      toast.error("Could not read that image");
    }
  };

  const saveProfile = async (e) => {
    e?.preventDefault?.();
    setSavingProfile(true);
    try {
      const { data } = await api.patch("/auth/me", {
        name,
        avatar_url: avatarUrl || null,
      });
      setUser(data);
      toast.success("Profile updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update profile");
    } finally {
      setSavingProfile(false);
    }
  };

  const removeAvatar = async () => {
    setSavingProfile(true);
    try {
      const { data } = await api.patch("/auth/me", { avatar_url: null });
      setUser(data);
      setAvatarUrl("");
      toast.success("Profile picture removed");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to remove picture");
    } finally {
      setSavingProfile(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pwNew !== pwConfirm) {
      toast.error("New passwords do not match");
      return;
    }
    if (pwNew.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    setSavingPw(true);
    try {
      await api.post("/auth/change-password", {
        current_password: pwCurrent,
        new_password: pwNew,
      });
      toast.success("Password changed successfully");
      setPwCurrent("");
      setPwNew("");
      setPwConfirm("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to change password");
    } finally {
      setSavingPw(false);
    }
  };

  const initials = (user.name || "?")
    .split(" ")
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <div>
      <button
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 mb-4"
      >
        <ArrowLeft className="w-4 h-4" /> Back
      </button>
      <PageHeader
        title="My profile"
        subtitle="Update your display name, profile picture and password."
      />

      <div className="grid lg:grid-cols-2 gap-6">
        <SectionCard title="Profile">
          <form onSubmit={saveProfile} className="space-y-5">
            <div className="flex items-center gap-5">
              <div className="relative">
                {avatarUrl ? (
                  <img
                    src={avatarUrl}
                    alt="avatar"
                    data-testid="profile-avatar-img"
                    className="w-24 h-24 rounded-full object-cover border border-slate-200"
                  />
                ) : (
                  <div className="w-24 h-24 rounded-full bg-blue-50 grid place-items-center text-blue-700 text-2xl font-semibold border border-slate-200">
                    {initials || <UserIcon className="w-8 h-8" />}
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-2">
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  onChange={onPickAvatar}
                  className="hidden"
                  data-testid="profile-avatar-input"
                />
                <button
                  type="button"
                  data-testid="profile-avatar-pick"
                  onClick={() => fileRef.current?.click()}
                  className="inline-flex items-center gap-2 border border-slate-300 rounded-lg px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  <Camera className="w-4 h-4" /> Choose image
                </button>
                {avatarUrl && (
                  <button
                    type="button"
                    data-testid="profile-avatar-remove"
                    onClick={removeAvatar}
                    className="inline-flex items-center gap-2 text-sm text-red-600 hover:underline"
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Remove picture
                  </button>
                )}
                <p className="text-xs text-slate-500 leading-relaxed max-w-xs">
                  Images are auto-resized to 256×256 JPEG. Square photos work best.
                </p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Full name
              </label>
              <input
                data-testid="profile-name-input"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                className={inputCls}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Email
              </label>
              <input
                value={user.email}
                disabled
                className={`${inputCls} bg-slate-50 text-slate-500`}
              />
              <p className="text-xs text-slate-500 mt-1">Email cannot be changed.</p>
            </div>

            <div className="flex justify-end">
              <button
                data-testid="profile-save-button"
                type="submit"
                disabled={savingProfile}
                className="inline-flex items-center gap-2 bg-blue-600 text-white rounded-lg px-4 py-2 font-medium hover:bg-blue-700 disabled:opacity-60"
              >
                <Save className="w-4 h-4" />
                {savingProfile ? "Saving…" : "Save changes"}
              </button>
            </div>
          </form>
        </SectionCard>

        <SectionCard title="Change password">
          <form onSubmit={changePassword} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Current password
              </label>
              <input
                data-testid="pw-current-input"
                type="password"
                required
                value={pwCurrent}
                onChange={(e) => setPwCurrent(e.target.value)}
                className={inputCls}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                New password
              </label>
              <input
                data-testid="pw-new-input"
                type="password"
                required
                minLength={6}
                value={pwNew}
                onChange={(e) => setPwNew(e.target.value)}
                className={inputCls}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Confirm new password
              </label>
              <input
                data-testid="pw-confirm-input"
                type="password"
                required
                minLength={6}
                value={pwConfirm}
                onChange={(e) => setPwConfirm(e.target.value)}
                className={inputCls}
              />
            </div>
            <div className="flex justify-end">
              <button
                data-testid="pw-save-button"
                type="submit"
                disabled={savingPw}
                className="inline-flex items-center gap-2 bg-blue-600 text-white rounded-lg px-4 py-2 font-medium hover:bg-blue-700 disabled:opacity-60"
              >
                <Lock className="w-4 h-4" />
                {savingPw ? "Updating…" : "Update password"}
              </button>
            </div>
          </form>
        </SectionCard>
      </div>
    </div>
  );
}
